import React, { useEffect, useState } from "react";
import { FieldArray, Form, FormikProvider, useFormik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import {
  addSessionPreference,
  changeBatchStatus,
  getBatchesList,
  getCardsData,
  getSessionPreferences,
  getTutorsList,
} from "../../redux/features/batchSlice";
import { Icon } from "@iconify/react/dist/iconify.js";
import GlobalDialog from "../../components/mui/Dialogue/GlobalDialog";
import GlobalSelectField from "../../components/mui/GlobalSelectField";
import * as Yup from "yup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const SessionPreference = ({ rowData, dispatchData, open, setOpen }) => {
  // const [open, setOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, sessionPreferences } = useSelector((state) => state.batch);

  const BatchStatuses = [{ id: 2, name: "Temporary Inactive" }];

  const getStatus = (name) => {
    const match = BatchStatuses.find((status) => status.name === name);
    return match ? match.id : "";
  };

  const validationSchema = Yup.object().shape({
    // subject_enrollment_id: Yup.string().required("Choose a subject"),
    // sessionCount: Yup.string().required("Session count is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      batch_id: rowData?.course_batch_id,
      subjects: [
        {
          subject_enrollment_id: "",
          subject_name: "",
          batch_id: rowData?.course_batch_id,
          preferences: [],
          sessionCount: null,
        },
      ],
    },

    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        data: values.subjects.map((subj) => ({
          subject_enrollment_id: subj.subject_enrollment_id,
          batch_id: values.batch_id,
          sessionCount: subj.sessionCount,
          preferences: subj.preferences.map((dayId) => ({ week_day: dayId })),
        })),
      };

      dispatch(addSessionPreference(payload)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          dispatch(getCardsData());
          dispatch(getBatchesList(dispatchData));
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload.message, { variant: "error" });
        }
      });
    },
  });

  useEffect(() => {
    if (open) {
      dispatch(getSessionPreferences(rowData?.course_batch_id));
    }
  }, [open]);

  useEffect(() => {
    if (sessionPreferences?.length > 0) {
      formik.setValues({
        batch_id: rowData?.course_batch_id,
        subjects: sessionPreferences.map((sp) => ({
          subject_enrollment_id: sp.subject_enrollment_id,
          batch_id: sp.batch_id || rowData?.course_batch_id,
          sessionCount: sp.total_sessions,
          preferences: sp.preferences.map((p) => p.week_day),
        })),
      });
    } else {
      formik.setValues({
        batch_id: rowData?.course_batch_id,
        subjects: [
          {
            subject_enrollment_id: "",
            batch_id: rowData?.course_batch_id,
            preferences: [],
            sessionCount: null,
          },
        ],
      });
    }
  }, [sessionPreferences, rowData]);

  const weekdays = [
    { id: 0, name: "Sunday" },
    { id: 1, name: "Monday" },
    { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" },
    { id: 4, name: "Thursday" },
    { id: 5, name: "Friday" },
    { id: 6, name: "Saturday" },
  ];

  return (
    <GlobalDialog
      title={"Session Preference"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"sm"}
      closeButton={true}
      // popupButton={
      //   <Button
      //     size="small"
      //     variant="outlined"
      //     color="primary"
      //     onClick={() => setOpen(true)}
      //   >
      //     <Icon
      //       icon={"material-symbols-light:change-circle-outline"}
      //       width={18}
      //       height={18}
      //     />
      //     Change Status
      //   </Button>
      // }
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box>
                <FieldArray
                  name="subjects"
                  render={(arrayHelpers) => (
                    <Box>
                      {formik.values.subjects.map((subject, index) => (
                        <Stack
                          key={index}
                          spacing={2}
                          sx={{
                            mb: 2,
                            p: 2,
                            border: "1px solid #ddd",
                            borderRadius: 2,
                          }}
                        >
                          <FormControl fullWidth size="medium">
                            <InputLabel id={`subject-label-${index}`}>
                              Subject
                            </InputLabel>
                            <Select
                              value={subject.subject_enrollment_id}
                              label="Subject"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `subjects[${index}].subject_enrollment_id`,
                                  e.target.value
                                )
                              }
                            >
                              {rowData?.subjects?.map((s) => (
                                <MenuItem
                                  key={s.subject_enrollment_id}
                                  value={s.subject_enrollment_id}
                                >
                                  {s.subject_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            name={`subjects[${index}].sessionCount`}
                            value={formik.values.subjects[index].sessionCount}
                            error={
                              formik.touched.subjects?.[index]?.sessionCount &&
                              Boolean(
                                formik.errors.subjects?.[index]?.sessionCount
                              )
                            }
                            helperText={
                              formik.touched.subjects?.[index]?.sessionCount &&
                              formik.errors.subjects?.[index]?.sessionCount
                            }
                            onChange={(e) =>
                              formik.setFieldValue(
                                `subjects[${index}].sessionCount`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="medium"
                            label="Total Session"
                            placeholder="Enter session count"
                            InputLabelProps={{ shrink: true }}
                          />

                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={weekdays}
                            getOptionLabel={(option) => option.name}
                            value={weekdays.filter((day) =>
                              subject.preferences.includes(day.id)
                            )}
                            onChange={(event, newValue) => {
                              formik.setFieldValue(
                                `subjects[${index}].preferences`,
                                newValue.map((day) => day.id)
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Day Preference"
                                placeholder="Choose days"
                                error={
                                  formik.touched.subjects?.[index]
                                    ?.preferences &&
                                  Boolean(
                                    formik.errors.subjects?.[index]?.preferences
                                  )
                                }
                                helperText={
                                  formik.touched.subjects?.[index]
                                    ?.preferences &&
                                  formik.errors.subjects?.[index]?.preferences
                                }
                              />
                            )}
                          />

                          {formik.values.subjects.length > 1 && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={
                                  <Icon
                                    width={18}
                                    height={18}
                                    icon="mdi:trash-can"
                                  />
                                }
                                onClick={() => arrayHelpers.remove(index)}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                        </Stack>
                      ))}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          startIcon={
                            <Icon width={18} height={18} icon="mdi:plus" />
                          }
                          onClick={() =>
                            arrayHelpers.push({
                              subject_enrollment_id: "",
                              subject_name: "",
                              preferences: [],
                            })
                          }
                        >
                          Add Preference
                        </Button>
                      </Box>
                    </Box>
                  )}
                />

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}
                >
                  <Stack direction={"row"} spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={loading}
                      variant="contained"
                      color="secondary"
                      size="small"
                      type="submit"
                    >
                      {loading ? "Please wait..." : "Save"}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default SessionPreference;
